/** Sentinel owner id standing for the operating company (rows whose
 *  `company_id` is null / admin-owned). It sits alongside real contractor
 *  company ids in `OwnerFilterCriteria.ownerIds` so a single multi-select can
 *  offer "Operating company" next to every contractor company. Chosen to never
 *  collide with a real id. */
export const ADMIN_OWNER = '__operating__';

export type OwnerFilterCriteria = {
  /** Selected owners. Each entry is a contractor company id, or ADMIN_OWNER for
   *  the operating company (company_id null). Empty = no owner condition (all
   *  owners). Multiple entries OR together. */
  ownerIds: string[];
  /** Selected row statuses. Empty = no status condition (all statuses).
   *  Multiple entries OR together. */
  statuses: string[];
  /** Free-text search; '' = no search. Matched against the caller-supplied
   *  searchText(row), case-insensitively. */
  query: string;
};

/** The owner "bucket" a row belongs to: its company id, or ADMIN_OWNER when it
 *  has no company (operating-company / admin-owned). */
export function ownerKey(row: { company_id: string | null }): string {
  return row.company_id ?? ADMIN_OWNER;
}

/**
 * Filter an owner-tagged list (drivers or assets). The three facets — owner,
 * status, search — AND together; within the owner and status facets the chosen
 * values OR together (an empty facet imposes no condition). The row's searchable
 * text is supplied by `searchText` so this stays generic across entities
 * (drivers search name/email; assets search make/model/plate). Pure — never
 * mutates `rows`.
 */
export function filterByOwner<T extends { company_id: string | null; status: string }>(
  rows: T[],
  criteria: OwnerFilterCriteria,
  searchText: (row: T) => string,
): T[] {
  const q = criteria.query.trim().toLowerCase();
  const ownerSet = new Set(criteria.ownerIds);
  const statusSet = new Set(criteria.statuses);

  return rows.filter((row) => {
    // Owner facet — empty means "all owners"; otherwise the row's owner bucket
    // (its company id, or ADMIN_OWNER when company-less) must be one of those
    // chosen.
    if (ownerSet.size > 0 && !ownerSet.has(ownerKey(row))) return false;

    // Status facet — empty means "all statuses"; otherwise the row's status must
    // be one of the chosen ones. (This is the pattern to mirror for owners.)
    if (statusSet.size > 0 && !statusSet.has(row.status)) return false;

    // Search facet.
    if (q && !searchText(row).toLowerCase().includes(q)) return false;

    return true;
  });
}
